import { useSelector } from '@/api/state';
import { usePermissions } from '@/hooks/usePermissions';
import {
  AnchorButton,
  Button,
  ManageableItemsList,
  Modal,
  TTableRowAction,
  useToastContext,
} from '@payaca/ambrosia';

import Tooltip from '@payaca/components/tooltip/Tooltip';
import { ServicePlanPermissions } from '@payaca/permissions/service-plans/service-plans.permissions';
import * as servicePlansActions from '@payaca/store/servicePlans/servicePlansActions';
import { PublicHydratedServicePlanDiscountCode } from '@payaca/types/service-plans';
import moment from 'moment-timezone';
import { FC, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { Link } from 'react-router-dom';

const ServicePlanDiscountCodesListed: FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { pushInfo } = useToastContext();
  const [isDeletingServicePlan, setIsDeletingServicePlan] = useState(false);
  const [showDeleteDiscountCodeModal, setShowDeleteDiscountCodeModal] =
    useState<false | PublicHydratedServicePlanDiscountCode['publicId']>(false);

  const isGettingListedDiscountCodes = useSelector(
    (state) => state.servicePlans.isGettingListedDiscountCodes
  );
  const listedDiscountCodes = useSelector(
    (state) => state.servicePlans.listedDiscountCodes
  );

  const { userHasRequiredPermission } = usePermissions();

  useEffect(() => {
    dispatch(servicePlansActions.getListedDiscountCodes.request({}));
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, []);

  const tableQuickActions = useMemo(() => {
    const actions: TTableRowAction<PublicHydratedServicePlanDiscountCode>[] = [
      {
        label: 'Copy code',
        onClick: (row) => {
          void navigator.clipboard
            .writeText(row.code)
            .then(() => pushInfo('Copied!'));
        },
      },
    ];

    if (userHasRequiredPermission([ServicePlanPermissions.PERSIST_DISCOUNT])) {
      actions.push({
        label: 'Delete',
        onClick: (row) => {
          setShowDeleteDiscountCodeModal(row.publicId);
        },
      });
    }

    return actions;
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has a missing dependency: 'pushInfo'
  }, [userHasRequiredPermission]);

  return (
    <>
      <ManageableItemsList>
        <ManageableItemsList.HeaderBar
          heading="Discount codes"
          buttons={
            userHasRequiredPermission([
              ServicePlanPermissions.PERSIST_DISCOUNT,
            ]) ? (
              <AnchorButton
                as={Link}
                to="/service-plans/discount-codes/new"
                size="sm"
              >
                Create discount code
              </AnchorButton>
            ) : undefined
          }
        />

        <ManageableItemsList.Table
          uniqueKey="publicId"
          isLoading={isGettingListedDiscountCodes}
          items={listedDiscountCodes}
          itemActions={tableQuickActions}
        >
          <ManageableItemsList.Table.Column header="Name" field="name" />
          <ManageableItemsList.Table.Column
            header="PercentOff"
            field="percentOff"
            render={(field) => field + '%'}
          />
          <ManageableItemsList.Table.Column
            header="Duration"
            field="duration"
            render={(field) => field.charAt(0).toUpperCase() + field.slice(1)}
          />
          <ManageableItemsList.Table.Column
            header="Duration in months"
            field="durationInMonths"
          />
          <ManageableItemsList.Table.Column<
            PublicHydratedServicePlanDiscountCode,
            'expiryDate'
          >
            header="Expiries"
            field="expiryDate"
            render={(expiryDate) =>
              expiryDate ? moment(expiryDate).format('DD MMMM YYYY') : undefined
            }
          />
          <ManageableItemsList.Table.Column<
            PublicHydratedServicePlanDiscountCode,
            'appliesTo'
          >
            header="Applies to"
            field="appliesTo"
            render={(appliesTo) =>
              appliesTo.length ? (
                <Tooltip text={appliesTo.map((i) => i.name).join(', ')}>
                  <>{`${appliesTo.length} service plan${
                    appliesTo.length ? 's' : ''
                  }`}</>
                </Tooltip>
              ) : (
                'All service plans'
              )
            }
          />
          <ManageableItemsList.Table.Column header="Code" field="code" />
        </ManageableItemsList.Table>
      </ManageableItemsList>
      <Modal
        title="Delete discount code"
        isOpen={!!showDeleteDiscountCodeModal}
        onClose={() => setShowDeleteDiscountCodeModal(false)}
      >
        <Modal.Body>
          <p>
            Are you sure you want to delete this service plan discount code?
          </p>

          <p>
            Deleting a discount code does not affect any customers who have
            already applied the discount; it means that new customers can’t
            redeem the discount.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Modal.Footer.Actions>
            <Button
              colour="negative"
              isProcessing={isDeletingServicePlan}
              replaceContentWhenProcessing
              disabled={isDeletingServicePlan}
              onClick={() => {
                if (!showDeleteDiscountCodeModal) return;

                setIsDeletingServicePlan(true);

                dispatch(
                  servicePlansActions.deleteServicePlanDiscountCode.request({
                    servicePlanDiscountCodePublicId:
                      showDeleteDiscountCodeModal,
                    callback: () => {
                      dispatch(
                        servicePlansActions.getListedDiscountCodes.request({})
                      );
                      setIsDeletingServicePlan(false);
                      setShowDeleteDiscountCodeModal(false);
                    },
                    onErrorCallback: () => {
                      pushInfo(
                        'Failed to delete discount code, try again later or contact support.'
                      );
                      setIsDeletingServicePlan(false);
                      setShowDeleteDiscountCodeModal(false);
                    },
                  })
                );
              }}
            >
              Delete
            </Button>
          </Modal.Footer.Actions>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ServicePlanDiscountCodesListed;
